// Every figure on the pricing section is declared here, once. Prices are in
// euros per contract year. The per-asset price printed on each card is derived
// from these two numbers rather than typed a second time, so the two can't
// drift apart.
export interface PricingTier {
	name: string
	// Unique source assets protected during the contract year.
	assets: number
	// Euros per year.
	price: number
	features: string[]
}

export const PRICING_TIERS: PricingTier[] = [
	{
		name: 'Enterprise S',
		assets: 250_000,
		price: 14_900,
		features: [
			'Unlimited watermark embedding',
			'Unlimited forensic decoding',
			'Unlimited recipient-specific copies',
			'Self-hosted Docker deployment',
			'Production use',
		],
	},
	{
		name: 'Enterprise M',
		assets: 1_000_000,
		price: 29_900,
		features: [
			'Everything in Enterprise S',
			'Large catalog protection',
			'Multi-application deployment',
			'Operational telemetry support',
			'Priority support',
		],
	},
	{
		name: 'Enterprise L',
		assets: 20_000_000,
		price: 99_900,
		features: [
			'Everything in Enterprise M',
			'High-availability deployment support',
			'Large-scale catalog licensing',
			'Performance qualification assistance',
			'Commercial deployment review',
		],
	},
]

// The card past the last priced tier: a library larger than the biggest one
// listed is quoted rather than priced from a table.
export interface CustomTier {
	name: string
	price: string
	note: string
}

export const CUSTOM_TIER: CustomTier = {
	name: 'Custom',
	price: 'On request',
	note: 'Volume, terms and deployment agreed with you.',
}

// What every priced tier carries whatever its size, printed on each of their
// cards. The quoted card leaves it out: what it covers is part of the quote.
export const TIER_INCLUDED: string[] = [
	'Unlimited watermark embedding',
	'Unlimited forensic decoding',
	'Self-hosted Docker deployment',
]

// The entry tier and the largest library, found rather than pointed at by
// index, so reordering or inserting a tier cannot silently change what the
// home page advertises.
export const ENTRY_TIER: PricingTier = PRICING_TIERS.reduce(
	( lowest: PricingTier, tier: PricingTier ): PricingTier => tier.price < lowest.price ? tier : lowest
)

export const LARGEST_TIER: PricingTier = PRICING_TIERS.reduce(
	( largest: PricingTier, tier: PricingTier ): PricingTier => tier.assets > largest.assets ? tier : largest
)

// Number of tiers above the entry one, named on the home page as the way up.
export const ENTERPRISE_TIER_COUNT: number = PRICING_TIERS.length - 1


// Formatting lives here too, so the pricing page and the home page teaser
// print the same figures the same way.

///@brief   "250k", "1M", "20M": every tier is a round number, so no decimals
///         are needed.
export function formatAssets( assets: number ): string {
	if ( assets >= 1_000_000 ) return `${ assets / 1_000_000 }M`
	return `${ assets / 1_000 }k`
}

///@brief   Fixed locale: the site is in English and the grouping must not
///         change with the visitor's browser settings.
export function formatPrice( price: number ): string {
	return `€${ price.toLocaleString( 'en-US' ) }`
}

///@brief   Four decimals: the largest tier lands at €0.0050 and would print
///         as €0.01 with two.
export function formatPerAsset( tier: PricingTier ): string {
	return `€${ ( tier.price / tier.assets ).toFixed( 4 ) }`
}

///@brief   A tier on one line, "Enterprise S (250k assets, €14,900 / year)":
///         what a trial request says when it follows a click on its card.
export function describeTier( tier: PricingTier ): string {
	return `${ tier.name } (${ formatAssets( tier.assets ) } assets, ${ formatPrice( tier.price ) } / year)`
}

///@brief   The same for the quoted card.
export function describeCustomTier(): string {
	return `${ CUSTOM_TIER.name } (${ CUSTOM_TIER.price.toLowerCase() })`
}


// ---------------------------------------------------------------------------
// The pipeline without the mark.
//
// The same service with the watermark left out: resize, crop, blur, convert
// and deliver, for a platform that wants the processing before it wants the
// protection. It is priced on the cores it runs on rather than on assets,
// and it is the secondary offer: the protected tiers above are the product.
//
// The slider mirrors imgproxy Pro's, step for step: sixteen steps of sixteen
// cores, then unlimited. The rate is 80% of theirs, which is $50 a month or
// $500 a year per step, and every price stops short of the round figure the
// way a shop prices: a euro under for the month, €39 rather than €40, ten
// under for the year, €390 rather than €400, so the year stays exactly ten
// months' worth and paying by the year saves two months against paying by
// the month. Every fifth step, 80, 160 and 240 cores, is a landmark of the
// slider and stops further under: nine for the month, ninety for the year.
// Unlimited has a flat price of its own rather than a quote, a hundred under
// its round figure.

///@brief   Cores added by each step of the slider.
export const CORES_PER_STEP: number = 16

///@brief   Priced steps of the slider.
export const CORE_STEPS: number = 16

///@brief   The slider's last position, one past the priced steps: as many
///         cores as needed, at one flat price.
export const UNLIMITED_STEP: number = CORE_STEPS + 1

const MONTHLY_PER_STEP: number = 40
const YEARLY_PER_STEP: number = 400

///@brief   How far under the round figure each price stops.
const MONTHLY_OFF: number = 1
const YEARLY_OFF: number = 10

///@brief   Every fifth step is a landmark and stops further under.
const LANDMARK_EVERY: number = 5
const LANDMARK_MONTHLY_OFF: number = 9
const LANDMARK_YEARLY_OFF: number = 90

function isLandmarkStep( step: number ): boolean {
	return step % LANDMARK_EVERY === 0
}

const UNLIMITED_MONTHLY_PRICE: number = 990
const UNLIMITED_YEARLY_PRICE: number = 9_900

///@brief   Whether `step` is the unlimited position.
export function isUnlimitedStep( step: number ): boolean {
	return step >= UNLIMITED_STEP
}

///@brief   The cores at `step` as printed: "16", "32"... "Unlimited".
export function formatCores( step: number ): string {
	return isUnlimitedStep( step ) ? 'Unlimited' : String( step * CORES_PER_STEP )
}

///@brief   Euros a month at `step`: €39, €79, €119, €159, €191... €639,
///         then €990.
export function monthlyPriceAtStep( step: number ): number {
	if ( isUnlimitedStep( step ) ) return UNLIMITED_MONTHLY_PRICE
	return step * MONTHLY_PER_STEP - ( isLandmarkStep( step ) ? LANDMARK_MONTHLY_OFF : MONTHLY_OFF )
}

///@brief   Euros a year at `step`: €390, €790, €1,190, €1,590, €1,910...
///         €6,390, then €9,900.
export function yearlyPriceAtStep( step: number ): number {
	if ( isUnlimitedStep( step ) ) return UNLIMITED_YEARLY_PRICE
	return step * YEARLY_PER_STEP - ( isLandmarkStep( step ) ? LANDMARK_YEARLY_OFF : YEARLY_OFF )
}

///@brief   Months the yearly rate gives away against twelve monthly
///         payments: the badge printed by the yearly figure.
export const YEARLY_FREE_MONTHS: number = 12 - Math.round( YEARLY_PER_STEP / MONTHLY_PER_STEP )

///@brief   What the plan carries whatever its size, and what it leaves out.
export const PROCESSING_INCLUDED: string[] = [
	'Every processing operation, unmetered',
	'Self-hosted Docker deployment',
	'Production use',
]

export const PROCESSING_EXCLUDED: string = 'No watermark embedding or decoding: add them any time by moving to a protected tier.'

///@brief   The plan at `step` on one line, "Processing only (64 cores,
///         €1,590 / year)": what a trial request says when it follows a
///         click on the calculator's button.
export function describeProcessingPlan( step: number ): string {
	return `Processing only (${ formatCores( step ).toLowerCase() } cores, ${ formatPrice( yearlyPriceAtStep( step ) ) } / year)`
}
